import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useEffectEvent, useState, type FormEvent } from "react";
import ArrowLeft from "~icons/ph/arrow-left";
import CaretDown from "~icons/ph/caret-down";
import DeviceMobile from "~icons/ph/device-mobile";
import Fingerprint from "~icons/ph/fingerprint";
import { useAppLocation } from "../app/location";
import { useShell, type JoinTarget } from "../app/shell";
import {
  useConnection,
  usePasskeySignIn,
  useRegister,
  useRestore,
  useSubmitting,
  useWaitForApproval,
} from "../data/mutations";
import { useProtection } from "../data/session";
import { t } from "../i18n";
import { FormError, Modal } from "../ui/components";
import { notify } from "../ui/toast";
import { TurnstileChallenge } from "./turnstile";

function JoinStep({
  target,
  name,
  username,
  onName,
  onUsername,
  onRestore,
}: {
  target: JoinTarget | undefined;
  name: string;
  username: string;
  onName: (name: string) => void;
  onUsername: (username: string) => void;
  onRestore: () => void;
}) {
  const { close, createCallLink } = useShell();
  const register = useRegister(),
    connection = useConnection();
  const submitting = useSubmitting();
  const { protection, failed: protectionFailed, retry } = useProtection();
  const [turnstileToken, setTurnstileToken] = useState(""),
    [challengeAttempt, setChallengeAttempt] = useState(0),
    [error, setError] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (submitting || !protection || (protection.required && !turnstileToken))
      return;
    setError("");
    try {
      const data = await register.mutateAsync({
        action: "register",
        name,
        username: username.trim() || undefined,
        turnstileToken: turnstileToken || undefined,
      });
      if (target && target !== "link") {
        await connection.mutateAsync({
          action: "request",
          peer: target.person.id,
          code: target.code,
        });
        notify(
          t("تم إرسال طلب إضافة إلى {name}", { name: target.person.name }),
          "success",
        );
      } else notify(t("مرحبًا، {name}", { name: data.me.name }), "success");
      close();
      if (target === "link") void createCallLink();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      register.reset();
      setTurnstileToken("");
      setChallengeAttempt((n) => n + 1);
    }
  }
  return (
    <>
      <h2>{t("اختر اسمك")}</h2>
      <p className="modal-description">
        {target === "link"
          ? t("اختر الاسم الذي يراه من يفتح رابطك.")
          : target
            ? t("أدخل اسمك لإرسال طلب إضافة إلى {name}.", {
                name: target.person.name,
              })
            : t("استخدم اسمًا مستعارًا للبدء.")}
      </p>
      <form onSubmit={submit}>
        <label>
          {t("اسمك")}{" "}
          <input
            value={name}
            onChange={(e) => onName(e.target.value)}
            minLength={2}
            maxLength={40}
            required
            data-autofocus
            placeholder={t("الاسم الظاهر للأصدقاء")}
            autoComplete="nickname"
          />
        </label>
        <details className="optional-field">
          <summary>
            <CaretDown width={12} height={12} aria-hidden="true" />
            {t("تحديد اسم المستخدم")} <span>{t("اختياري")}</span>
          </summary>
          <label>
            <span className="sr-only">{t("اسم المستخدم")}</span>
            <input
              value={username}
              onChange={(e) => onUsername(e.target.value.toLowerCase())}
              pattern="[a-z0-9_]{3,24}"
              minLength={3}
              maxLength={24}
              placeholder="your_username"
              dir="ltr"
              autoComplete="username"
            />
          </label>
          <small>{t("حروف إنجليزية وأرقام وشرطة سفلية، دون مسافات.")}</small>
        </details>
        {!protection && !protectionFailed && (
          <p className="join-note" role="status">
            {t("جارٍ تحميل التحقق…")}
          </p>
        )}
        {(protectionFailed ||
          (protection?.required && !protection.siteKey)) && (
          <p className="form-error" role="alert">
            {t("التحقق غير متاح حاليًا. حاول لاحقًا.")}{" "}
            <button type="button" className="quiet-button" onClick={retry}>
              {t("إعادة المحاولة")}
            </button>
          </p>
        )}
        {protection?.required && protection.siteKey && (
          <TurnstileChallenge
            key={challengeAttempt}
            siteKey={protection.siteKey}
            onToken={setTurnstileToken}
          />
        )}
        <FormError message={error} />
        <button
          className="button full"
          aria-label={t("متابعة")}
          aria-busy={submitting}
          disabled={
            submitting ||
            !protection ||
            (protection.required && !turnstileToken)
          }
        >
          {submitting ? (
            <span className="action-skeleton" aria-hidden="true" />
          ) : (
            <>
              {t("متابعة")}{" "}
              <ArrowLeft className="directional" width={16} height={16} />
            </>
          )}
        </button>
      </form>
      <p className="join-note">
        {t("لا تحتاج إلى بريد إلكتروني أو كلمة مرور.")}
      </p>
      <button className="restore-link" onClick={onRestore}>
        {t("لديك حساب؟ سجّل الدخول")}{" "}
      </button>
    </>
  );
}

type PhoneWait =
  | { state: "starting" }
  | { state: "waiting"; code: string }
  | { state: "over"; message: string };

// A phone signed in to this account lets this browser in, with nothing from the
// browser's or the system's passkey support.
function PhoneStep({ onBack }: { onBack: () => void }) {
  const { close } = useShell();
  const wait = useWaitForApproval();
  const [attempt, setAttempt] = useState(0),
    [phone, setPhone] = useState<PhoneWait>({ state: "starting" });
  // The name shows whose account this browser just entered.
  const signedIn = useEffectEvent((name: string) => {
    close();
    notify(t("مرحبًا، {name}", { name }), "success");
  });
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    wait(signal, (code) => setPhone({ state: "waiting", code })).then(
      (account) => {
        if (account) signedIn(account.me.name);
        else if (!signal.aborted)
          setPhone({ state: "over", message: "انتهت مدة هذا الرمز." });
      },
      (error: Error) => {
        if (!signal.aborted)
          setPhone({ state: "over", message: error.message });
      },
    );
    return () => controller.abort();
  }, [attempt, wait]);
  return (
    <>
      <h2>{t("الدخول بهاتفك")}</h2>
      <p className="modal-description">
        {t(
          "امسح الرمز بهاتف دخلت منه إلى حسابك على رنّة، أو أدخل الرمز في صفحة حسابك عليه.",
        )}
      </p>
      {phone.state === "waiting" ? (
        <>
          {/* Scanners read dark modules on a light tile most reliably. */}
          <div className="profile-qr">
            <QRCodeSVG
              value={new URL(`/approve/${phone.code}`, location.origin).href}
              size={168}
              level="M"
              bgColor="#edf1ef"
              fgColor="#151817"
              title={t("رمز QR لتسجيل الدخول")}
            />
          </div>
          <p className="device-code" dir="ltr">
            {phone.code.slice(0, 4)}-{phone.code.slice(4)}
          </p>
        </>
      ) : (
        phone.state === "starting" && (
          <div className="profile-link-skeleton" aria-hidden="true">
            <i />
          </div>
        )
      )}
      <p className="join-note" role="status">
        {phone.state === "starting"
          ? t("جارٍ تجهيز الرمز…")
          : phone.state === "waiting"
            ? t("في انتظار موافقة هاتفك…")
            : t(phone.message)}
      </p>
      {phone.state === "over" && (
        <button
          className="button full device-new-code"
          data-autofocus
          onClick={() => {
            setPhone({ state: "starting" });
            setAttempt((n) => n + 1);
          }}
        >
          {t("رمز جديد")}
        </button>
      )}
      <button className="restore-link" onClick={onBack}>
        {t("رجوع")}
      </button>
    </>
  );
}

function RestoreStep({
  code,
  onCode,
  onJoin,
  onPhone,
}: {
  code: string;
  onCode: (code: string) => void;
  onJoin: () => void;
  onPhone: () => void;
}) {
  const { close } = useShell();
  // A person here is approving another device, so this one has no phone to ask.
  const { signInCode } = useAppLocation();
  const restore = useRestore(),
    passkey = usePasskeySignIn();
  const submitting = useSubmitting();
  const [error, setError] = useState("");
  const passkeys = browserSupportsWebAuthn();
  function signedIn() {
    close();
    notify(t("تم تسجيل الدخول"), "success");
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");
    try {
      await restore.mutateAsync(code);
      signedIn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      restore.reset();
    }
  }
  async function signInWithPasskey() {
    setError("");
    try {
      if (await passkey.mutateAsync()) signedIn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      passkey.reset();
    }
  }
  const codeForm = (
    <form onSubmit={submit}>
      <label>
        {t("رمز الاستعادة")}{" "}
        <input
          dir="ltr"
          value={code}
          onChange={(e) => onCode(e.target.value)}
          data-autofocus={passkeys ? undefined : true}
          autoComplete="off"
          spellCheck={false}
          required
          minLength={43}
          maxLength={60}
        />
      </label>
      <button
        className={passkeys ? "button secondary full" : "button full"}
        disabled={submitting}
      >
        {t("متابعة")}{" "}
        <ArrowLeft className="directional" width={16} height={16} />
      </button>
    </form>
  );
  const phoneOption = !signInCode && (
    <button
      className="button secondary full device-phone-option"
      disabled={submitting}
      onClick={onPhone}
    >
      <DeviceMobile width={16} height={16} aria-hidden="true" />
      {t("الدخول بهاتفك")}
    </button>
  );
  return (
    <>
      <h2>{t("تسجيل الدخول")}</h2>
      <p className="modal-description">
        {t(
          passkeys
            ? "ادخل ببصمتك أو وجهك أو قفل الشاشة."
            : "أدخل رمز الاستعادة الذي حفظته.",
        )}{" "}
      </p>
      {passkeys ? (
        <>
          <button
            className="button full"
            data-autofocus
            disabled={submitting}
            onClick={() => void signInWithPasskey()}
          >
            <Fingerprint width={16} height={16} aria-hidden="true" />
            {t("الدخول بمفتاح مرور")}
          </button>
          {phoneOption}
          <details className="optional-field">
            <summary>
              <CaretDown width={12} height={12} aria-hidden="true" />
              {t("استخدام رمز الاستعادة")}
            </summary>
            {codeForm}
          </details>
        </>
      ) : (
        <>
          {codeForm}
          {phoneOption}
        </>
      )}
      <FormError message={error} />
      <button className="restore-link" onClick={onJoin}>
        {t("جديد هنا؟ انضم")}{" "}
      </button>
    </>
  );
}

// Moving between joining and signing in keeps what was typed in each.
export function EnterDialog({
  step: start,
  target,
}: {
  step: "join" | "restore";
  target?: JoinTarget;
}) {
  const { close } = useShell();
  const [step, setStep] = useState<"join" | "restore" | "phone">(start),
    [name, setName] = useState(""),
    [username, setUsername] = useState(""),
    [code, setCode] = useState("");
  return (
    <Modal
      key={step}
      title={t(
        step === "join"
          ? "الدخول باسم مستعار"
          : step === "phone"
            ? "الدخول بهاتفك"
            : "تسجيل الدخول",
      )}
      onClose={close}
    >
      {step === "join" ? (
        <JoinStep
          target={target}
          name={name}
          username={username}
          onName={setName}
          onUsername={setUsername}
          onRestore={() => setStep("restore")}
        />
      ) : step === "phone" ? (
        <PhoneStep onBack={() => setStep("restore")} />
      ) : (
        <RestoreStep
          code={code}
          onCode={setCode}
          onJoin={() => setStep("join")}
          onPhone={() => setStep("phone")}
        />
      )}
    </Modal>
  );
}
